import {
  AddGroupMembershipRequest,
  BulkResult,
  GroupMembershipResponse,
  Pot,
  UpdateGroupMembership,
  useToast,
} from '@aslanpay/portal-common-lib';

import { collectFailedMembers, FailedMember } from 'app/utils/groupMembership';

import useGroups from '../useGroups';
import { GroupWithPot } from '../useGroupsWithPot/types';
import usePoints from '../usePoints';

import usePots from '.';

type Toast = ReturnType<typeof useToast>;

export type CreatePotWithPotManagersAndBalanceParams = {
  pointsToTransfer: number;
  changeMembers: UpdateGroupMembership[];
  addMembers: AddGroupMembershipRequest;
  companyPot?: Pot;
  group?: Omit<GroupWithPot, 'children'>;
  memberNamesById?: Map<string, string>;
};

export type CreatePotWithPotManagersAndBalanceCallbacks = {
  onSuccess?: (result?: { failedMembers: FailedMember[] }) => void;
  onError?: () => void;
};

const useCreatePotWithPotManagersAndBalance = (
  companyId: string,
  groupId?: string,
  toast?: Toast
) => {
  const { mutate: createPot } = usePots.useCreatePot();
  const { mutate: updateGroupMemberships } = useGroups.useUpdateGroupMemberships(
    companyId,
    groupId ?? ''
  );
  const { mutate: addGroupMemberships } = useGroups.useAddGroupMemberships(
    companyId,
    groupId ?? ''
  );
  const { mutate: transferPoints } = usePoints.useTransferPoints({ retry: 2 });

  const execute = (
    {
      pointsToTransfer,
      changeMembers = [],
      addMembers = [],
      companyPot,
      group,
      memberNamesById,
    }: CreatePotWithPotManagersAndBalanceParams,
    callbacks?: CreatePotWithPotManagersAndBalanceCallbacks
  ) => {
    if (!group) {
      throw new Error('Group is required to create pot with managers and balance');
    }

    // Resolves once every membership CALL returns 200 (with the per-row failures aggregated), and rejects only
    // when a whole call fails at the transport level — the caller keeps its original abort path for that.
    const runMembershipUpdates = (
      onDone: (failedManagers: FailedMember[]) => void,
      onFail: () => void
    ) => {
      const tasks: Promise<BulkResult<GroupMembershipResponse> | undefined>[] = [];
      if (changeMembers.length > 0) {
        tasks.push(
          new Promise((resolve, reject) => {
            updateGroupMemberships(changeMembers, {
              onSuccess: (data) => resolve(data),
              onError: () => reject(),
            });
          })
        );
      }
      if (addMembers.length > 0) {
        tasks.push(
          new Promise((resolve, reject) => {
            addGroupMemberships(addMembers, {
              onSuccess: (data) => resolve(data),
              onError: () => reject(),
            });
          })
        );
      }
      Promise.all(tasks)
        .then((results) => onDone(collectFailedMembers(results, (id) => memberNamesById?.get(id))))
        .catch(onFail);
    };

    createPot(
      {
        companyId,
        name: group.name || '',
        description: '',
        relatedId: String(group.id) || '',
        relatedType: 'GROUP',
      },
      {
        onSuccess: (pot) => {
          runMembershipUpdates(
            (failedManagers) => {
              transferPoints(
                {
                  fromPotId: companyPot?.id || '',
                  toPotId: pot.id,
                  points: pointsToTransfer,
                  companyId,
                },
                {
                  onSuccess: () => {
                    if (failedManagers.length === 0) {
                      toast?.({
                        position: 'top-right',
                        status: 'success',
                        title: 'Pot created',
                        description: 'The pot was successfully created.',
                      });
                    } else {
                      toast?.({
                        position: 'top-right',
                        status: 'warning',
                        title: 'Pot created',
                        description: `${failedManagers.length} pot manager(s) could not be added. See the reasons below.`,
                      });
                    }
                    callbacks?.onSuccess?.({ failedMembers: failedManagers });
                  },
                  onError: () => {
                    toast?.({
                      position: 'top-right',
                      status: 'error',
                      title: 'Transfer failed',
                      description:
                        'Pot and managers were set, but we failed to transfer points. Please try again.',
                    });
                    callbacks?.onError?.();
                  },
                }
              );
            },
            () => {
              toast?.({
                position: 'top-right',
                status: 'error',
                title: 'Update managers failed',
                description:
                  'Pot was created, but we failed to set pot managers or transfer amount. Please try again.',
              });
              callbacks?.onError?.();
            }
          );
        },
        onError: () => {
          toast?.({
            position: 'top-right',
            status: 'error',
            title: 'Create pot failed',
            description: 'Sorry, we failed to create pot. Please try again.',
          });
          callbacks?.onError?.();
        },
      }
    );
  };

  return { execute };
};

export default useCreatePotWithPotManagersAndBalance;
